import { useState, useCallback } from "react";

// Centralized scroll wait time (in milliseconds) before starting tour guide
// Set to 0 for immediate start, or higher value (e.g., 600) to wait for scroll animation
const TOUR_GUIDE_SCROLL_WAIT_TIME = 0;
export const BG_OVERLAY = {
  BACKGROUND: "rgba(0, 0, 0, 0.5)",
  BACKDROP_FILTER: "blur(1px)",
};

export interface TourStep {
  id: string;
  targetSelector?: string; // CSS selector for the element to highlight
  targetRef?: React.RefObject<HTMLElement>; // React ref for the element
  title: string;
  description: string;
  position?: "top" | "bottom" | "left" | "right" | "center";
  offset?: { x: number; y: number };
}

export interface TourGuideConfig {
  moduleId: string; // e.g., "legalChatbot"
  steps: TourStep[];
  enabled?: boolean; // Condition to enable/disable tour
}

export interface UseTourGuideReturn {
  isActive: boolean;
  currentStep: number;
  currentStepData: TourStep | null;
  startTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;
  totalSteps: number;
}

export const useTourGuide = (config: TourGuideConfig): UseTourGuideReturn => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const totalSteps = config.steps.length;

  const startTour = useCallback(() => {
    if (config.enabled !== false && config.steps.length > 0) {
      // Scroll the scrollable container to top (the Box with class "right-box" in DashboardLayout)
      const scrollableContainer = document.querySelector(
        ".right-box"
      ) as HTMLElement;
      if (scrollableContainer) {
        scrollableContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });

        // Wait for scroll animation to complete before starting the tour
        setTimeout(() => {
          setIsActive(true);
          setCurrentStep(0);
        }, TOUR_GUIDE_SCROLL_WAIT_TIME);
      } else {
        // Fallback to window scroll if container not found
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        setTimeout(() => {
          setIsActive(true);
          setCurrentStep(0);
        }, TOUR_GUIDE_SCROLL_WAIT_TIME);
      }
    }
  }, [config.enabled, config.steps.length]);

  const stopTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev < totalSteps - 1) {
        return prev + 1;
      } else {
        setIsActive(false);
        return prev;
      }
    });
  }, [totalSteps]);

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev > 0) {
        return prev - 1;
      }
      return prev;
    });
  }, []);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < totalSteps) {
        setCurrentStep(stepIndex);
      }
    },
    [totalSteps]
  );

  const currentStepData =
    isActive && config.steps[currentStep] ? config.steps[currentStep] : null;

  return {
    isActive,
    currentStep,
    currentStepData,
    startTour,
    stopTour,
    nextStep,
    previousStep,
    goToStep,
    totalSteps,
  };
};
